import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  Zap, GraduationCap, Home, Calculator, Utensils, HardHat, Megaphone, Heart, Scale,
  Search, Bookmark, BookmarkCheck, ArrowRight, Clock, Loader2, Wand2, Rocket,
} from "lucide-react";
import { AIResourcesShell, StepLabel, ToolBadge } from "../../components/ai/AIResourcesShell";
import { workflowAudit, workflowSearch, workflowExpand, aiSave, aiListSaved } from "../../lib/api";
import { useAuth } from "../../hooks/useAuth";
import { useAIResources } from "../../hooks/useAIResources";

const PROFESSIONS = [
  { label: "Teacher", icon: GraduationCap }, { label: "Realtor", icon: Home },
  { label: "Accountant", icon: Calculator }, { label: "Restaurant", icon: Utensils },
  { label: "Construction", icon: HardHat }, { label: "Marketing", icon: Megaphone },
  { label: "Nurse", icon: Heart }, { label: "Lawyer", icon: Scale },
];
const SEARCH_CHIPS = ["Email", "Scheduling", "Marketing", "Payroll", "Inventory", "Customer Support", "Social Media", "Data Entry"];

export default function AIWorkflow() {
  const { user, requireAuth } = useAuth();
  const { workflow, patchWorkflow } = useAIResources();
  const { profession, customRole, audit, extra, search, vision, custom } = workflow;
  const setProfession = (v) => patchWorkflow({ profession: v });
  const setCustomRole = (v) => patchWorkflow({ customRole: v });
  const setAudit = (v) => patchWorkflow({ audit: v });
  const setExtra = (v) => patchWorkflow({ extra: v });
  const setSearch = (v) => patchWorkflow({ search: v });
  const setVision = (v) => patchWorkflow({ vision: v });
  const setCustom = (v) => patchWorkflow({ custom: v });
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [searching, setSearching] = useState(false);
  const [expanding, setExpanding] = useState(false);
  const [saved, setSaved] = useState([]);

  const loadSaved = useCallback(() => {
    if (!user) { setSaved([]); return; }
    aiListSaved("workflow").then(setSaved).catch(() => setSaved([]));
  }, [user]);
  useEffect(() => { loadSaved(); }, [loadSaved]);

  const runAudit = async (prof) => {
    const p = (prof || customRole).trim();
    if (!p) { toast.error("Tell us what you do for work"); return; }
    setProfession(p); setLoadingAudit(true); setAudit(null); setExtra([]);
    try { setAudit(await workflowAudit(p)); }
    catch { toast.error("Could not generate workflows. Try again."); }
    finally { setLoadingAudit(false); }
  };

  const doSearch = async (q) => {
    const query = (q || search).trim();
    if (!query) return;
    setSearch(query); setSearching(true);
    try { const w = await workflowSearch(query, profession); setExtra([{ ...w, _k: Date.now() }, ...extra]); }
    catch { toast.error("Search failed"); }
    finally { setSearching(false); }
  };

  const doExpand = async () => {
    if (!vision.trim()) { toast.error("Describe the task first"); return; }
    setExpanding(true);
    try { setCustom(await workflowExpand(vision.trim(), profession)); }
    catch { toast.error("Could not generate workflow"); }
    finally { setExpanding(false); }
  };

  const saveWorkflow = (wf) => {
    requireAuth(async () => {
      try { await aiSave("workflow", wf); toast.success("Saved to your workflows"); loadSaved(); }
      catch { toast.error("Could not save"); }
    });
  };

  const sidebar = (
    <>
      <div className="rounded-2xl bg-gradient-to-br from-[#0F0F0F] to-[#23200f] text-white p-5">
        <Rocket size={20} className="text-[#D4AF37]" />
        <p className="font-display text-lg font-bold mt-2">Save 5+ Hours</p>
        <p className="text-sm text-[#c9c39f] mt-1 leading-snug">Discover AI workflows that save you time every week and help you grow faster.</p>
        <button onClick={() => document.getElementById("wf-step1")?.scrollIntoView({ behavior: "smooth" })} className="mt-4 w-full bg-[#D4AF37] text-[#0F0F0F] rounded-full py-2.5 text-sm font-bold hover:bg-[#c5a030] transition-colors" data-testid="sidebar-start-audit">
          Start Workflow Audit
        </button>
      </div>

      <div className="rounded-2xl border border-gray-100 bg-white p-5">
        <div className="flex items-center justify-between">
          <p className="font-display font-bold text-[#0F0F0F]">My Saved Workflows</p>
          <span className="text-xs bg-[#F7F7F7] rounded-full px-2 py-0.5 text-[#687280]" data-testid="saved-workflow-count">{saved.length}</span>
        </div>
        <div className="mt-3 space-y-2">
          {saved.length === 0 ? (
            <p className="text-xs text-[#9aa1ab]">Save a workflow to see it here.</p>
          ) : saved.slice(0, 4).map((s) => (
            <div key={s.id} className="flex items-center gap-2 text-sm" data-testid={`saved-workflow-${s.id}`}>
              <BookmarkCheck size={14} className="text-[#D4AF37] shrink-0" />
              <div className="min-w-0">
                <p className="font-semibold text-[#0F0F0F] truncate">{s.data?.name}</p>
                <p className="text-[11px] text-[#9aa1ab]">Saves {s.data?.hours_saved} hrs/week</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-gray-100 bg-white p-5">
        <div className="flex items-center gap-2"><Wand2 size={16} className="text-[#D4AF37]" /><p className="font-semibold text-[#0F0F0F] text-sm">Need a custom workflow?</p></div>
        <p className="text-xs text-[#687280] mt-1.5">Describe a task that takes too much time and we'll build a workflow just for you.</p>
        <button onClick={() => document.getElementById("wf-step4")?.scrollIntoView({ behavior: "smooth" })} className="mt-3 w-full border border-gray-200 rounded-full py-2 text-xs font-semibold text-[#374151] hover:border-[#D4AF37] transition-colors">Create Custom</button>
      </div>
    </>
  );

  return (
    <AIResourcesShell active="workflow" title="AI Workflow" subtitle="Discover AI workflows that can save you 5+ hours every week." icon={Zap} extra={sidebar}>
      {/* STEP 1 */}
      <section id="wf-step1" className="rounded-2xl border border-gray-100 bg-white p-6" data-testid="wf-step1">
        <StepLabel n={1} />
        <h2 className="font-display text-xl font-bold text-[#0F0F0F] mt-1">What do you do for work?</h2>
        <p className="text-sm text-[#687280] mt-1">This helps us find the best AI workflows for you.</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 gap-2.5 mt-5">
          {PROFESSIONS.map((p) => {
            const on = profession === p.label;
            return (
              <button key={p.label} onClick={() => runAudit(p.label)} data-testid={`wf-profession-${p.label.toLowerCase()}`}
                className={`flex flex-col items-center gap-2 rounded-xl border py-4 transition-colors ${on ? "border-[#D4AF37] bg-[#FBF7EA]" : "border-gray-200 hover:border-[#D4AF37]"}`}>
                <p.icon size={20} className={on ? "text-[#D4AF37]" : "text-[#687280]"} />
                <span className="text-xs font-semibold text-[#374151]">{p.label}</span>
              </button>
            );
          })}
        </div>
        <div className="flex flex-col sm:flex-row gap-3 mt-4">
          <div className="relative flex-1">
            <input value={customRole} onChange={(e) => setCustomRole(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runAudit()} placeholder="Or type your role..." className="w-full rounded-full border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-[#D4AF37]" data-testid="wf-custom-role" />
            <Search size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#9aa1ab]" />
          </div>
          <button onClick={() => runAudit()} disabled={loadingAudit} className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full px-6 py-3 text-sm font-bold hover:bg-[#c5a030] transition-colors disabled:opacity-60" data-testid="wf-find-btn">
            {loadingAudit ? <Loader2 size={15} className="animate-spin" /> : <ArrowRight size={15} />} Find My Workflows
          </button>
        </div>
      </section>

      {loadingAudit && (
        <div className="flex items-center justify-center py-16 text-[#687280]" data-testid="wf-loading"><Loader2 className="animate-spin mr-2" /> Analyzing the best AI workflows…</div>
      )}

      {/* STEP 2 */}
      {audit && (
        <section className="mt-6" data-testid="wf-step2">
          <StepLabel n={2} />
          <div className="flex items-center gap-3 mt-1">
            <h2 className="font-display text-xl font-bold text-[#0F0F0F]">AI Workflow Audit</h2>
            <span className="text-[11px] font-semibold bg-[#0F0F0F] text-white px-2.5 py-1 rounded-full">Based on: {audit.profession}</span>
          </div>
          <p className="text-sm text-[#687280] mt-1">Here are 3 high-impact workflows you can implement today.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
            {audit.workflows.map((w, i) => <WorkflowCard key={i} w={w} onSave={() => saveWorkflow(w)} />)}
          </div>
          <div className="mt-4 rounded-2xl bg-[#FBF7EA] border border-[#D4AF37]/30 p-5 flex flex-wrap items-center gap-4" data-testid="wf-total">
            <span className="w-12 h-12 rounded-full bg-[#D4AF37] text-[#0F0F0F] flex items-center justify-center"><Clock size={22} /></span>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wide text-[#8a6d1a]">Estimated Time Saved</p>
              <p className="font-display text-2xl font-extrabold text-[#0F0F0F]">{audit.total_hours} Hours Every Week</p>
            </div>
            <div className="ml-auto text-right">
              <p className="font-display text-xl font-extrabold text-[#0F0F0F]">≈ {audit.yearly_hours} Hours / Year</p>
              <p className="text-xs text-[#8a6d1a]">More time for what matters most.</p>
            </div>
          </div>
        </section>
      )}

      {/* STEP 3 */}
      {audit && (
        <section className="mt-8 rounded-2xl border border-gray-100 bg-white p-6" data-testid="wf-step3">
          <StepLabel n={3} />
          <h2 className="font-display text-xl font-bold text-[#0F0F0F] mt-1">Find Another Workflow</h2>
          <p className="text-sm text-[#687280] mt-1">Search for a task or area you want to improve.</p>
          <div className="flex gap-3 mt-4">
            <input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doSearch()} placeholder="Search another workflow... (e.g. Email, Scheduling)" className="flex-1 rounded-full border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-[#D4AF37]" data-testid="wf-search-input" />
            <button onClick={() => doSearch()} disabled={searching} className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full px-6 py-3 text-sm font-bold hover:bg-[#c5a030] transition-colors disabled:opacity-60" data-testid="wf-search-btn">{searching ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />} Search</button>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {SEARCH_CHIPS.map((c) => <button key={c} onClick={() => doSearch(c)} className="text-xs px-3 py-1.5 rounded-full border border-gray-200 text-[#374151] hover:border-[#D4AF37] transition-colors" data-testid={`wf-chip-${c.toLowerCase().replace(/ /g, "-")}`}>{c}</button>)}
          </div>
          {extra.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
              {extra.map((w) => <WorkflowCard key={w._k} w={w} onSave={() => saveWorkflow(w)} />)}
            </div>
          )}
        </section>
      )}

      {/* STEP 4 */}
      <section id="wf-step4" className="mt-8 rounded-2xl border border-gray-100 bg-white p-6" data-testid="wf-step4">
        <StepLabel n={4} />
        <h2 className="font-display text-xl font-bold text-[#0F0F0F] mt-1">Expand Vision</h2>
        <p className="text-sm text-[#687280] mt-1">Describe a task that takes too much time. We'll build a workflow for you.</p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-4">
          <div>
            <textarea value={vision} maxLength={250} onChange={(e) => setVision(e.target.value)} rows={5} placeholder="Example: I spend hours replying to customer emails." className="w-full rounded-2xl border border-gray-200 p-4 text-sm focus:outline-none focus:border-[#D4AF37] resize-none" data-testid="wf-vision-input" />
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-[#9aa1ab]">{vision.length}/250</span>
              <button onClick={doExpand} disabled={expanding} className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full px-6 py-2.5 text-sm font-bold hover:bg-[#c5a030] transition-colors disabled:opacity-60" data-testid="wf-generate-btn">{expanding ? <Loader2 size={15} className="animate-spin" /> : <Wand2 size={15} />} Generate Workflow</button>
            </div>
          </div>
          {custom && (
            <div className="rounded-2xl border border-gray-100 bg-[#F7F7F7] p-5" data-testid="wf-custom-result">
              <div className="flex items-start justify-between">
                <p className="font-display font-bold text-[#0F0F0F]">Your Custom Workflow</p>
                <button onClick={() => saveWorkflow(custom)} className="text-[#687280] hover:text-[#D4AF37]" data-testid="wf-custom-save"><Bookmark size={18} /></button>
              </div>
              <div className="mt-3 space-y-2.5 text-sm">
                <div><p className="text-[11px] font-bold uppercase text-[#D4AF37]">Workflow Name</p><p className="text-[#0F0F0F] font-semibold">{custom.name}</p></div>
                <div><p className="text-[11px] font-bold uppercase text-[#D4AF37]">Problem</p><p className="text-[#374151]">{custom.problem}</p></div>
                <div><p className="text-[11px] font-bold uppercase text-[#D4AF37]">AI Solution</p><p className="text-[#374151]">{custom.solution}</p></div>
                <div className="flex items-center gap-2 text-[#8a6d1a] font-semibold"><Clock size={14} /> Saves {custom.hours_saved} hrs / week</div>
                {custom.tools?.length > 0 && (
                  <div><p className="text-[11px] font-bold uppercase text-[#D4AF37] mb-1.5">Recommended AI Tools</p><div className="flex gap-1.5">{custom.tools.map((t) => <ToolBadge key={t} name={t} />)}</div></div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Bottom banner */}
      <div className="mt-8 rounded-3xl bg-gradient-to-br from-[#0F0F0F] to-[#23200f] text-white p-6 md:p-8 flex flex-wrap items-center gap-4">
        <div>
          <p className="font-display text-xl font-extrabold text-[#D4AF37]">Work Smarter. Save Time. Build Faster.</p>
          <p className="text-sm text-[#c9c39f] mt-1">Implement these AI workflows and get back more than 5 hours every week.</p>
        </div>
        <button onClick={() => document.getElementById("wf-step1")?.scrollIntoView({ behavior: "smooth" })} className="ml-auto inline-flex items-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full px-6 py-3 text-sm font-bold hover:bg-[#c5a030] transition-colors">
          Run an Audit <ArrowRight size={15} />
        </button>
      </div>
    </AIResourcesShell>
  );
}

const WorkflowCard = ({ w, onSave }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-5 flex flex-col" data-testid="wf-card">
      <div className="flex items-start justify-between">
        <span className="w-10 h-10 rounded-xl bg-[#D4AF37]/12 text-[#B8901F] flex items-center justify-center"><Zap size={18} /></span>
        <button onClick={onSave} className="text-[#9aa1ab] hover:text-[#D4AF37]" data-testid="wf-card-save"><Bookmark size={17} /></button>
      </div>
      <h3 className="font-display font-bold text-[#0F0F0F] mt-3">{w.name}</h3>
      <p className="text-sm text-[#687280] mt-1 leading-snug flex-1">{w.description}</p>
      <p className="text-sm font-semibold text-[#8a6d1a] mt-3 flex items-center gap-1.5"><Clock size={14} /> Saves {w.hours_saved} Hours / Week</p>
      {w.tools?.length > 0 && (
        <div className="mt-3">
          <p className="text-[11px] font-bold uppercase text-[#9aa1ab] mb-1.5">Tools Used</p>
          <div className="flex gap-1.5">{w.tools.map((t) => <ToolBadge key={t} name={t} />)}</div>
        </div>
      )}
      <button onClick={() => setOpen((o) => !o)} className="mt-4 w-full inline-flex items-center justify-center gap-1.5 border border-gray-200 rounded-full py-2 text-sm font-semibold text-[#374151] hover:border-[#D4AF37] transition-colors" data-testid="wf-card-view">
        {open ? "Hide" : "View Workflow"} <ArrowRight size={14} />
      </button>
      {open && (
        <div className="mt-3 text-sm text-[#374151] bg-[#F7F7F7] rounded-xl p-3">
          <p className="font-semibold text-[#0F0F0F]">Recommended AI Tools</p>
          <ul className="mt-1.5 list-disc list-inside text-[#687280]">{(w.tools || []).map((t) => <li key={t}>{t}</li>)}</ul>
        </div>
      )}
    </div>
  );
};
